import React, {ReactNode, useCallback, useContext, useEffect, useState} from "react";
import {ForgotPasswordAttempt} from "../../../types";

interface ForgotPasswordContextValue {
    updateForgotPasswordSendMfa: (mfaType: string) => Promise<void>;
    updateForgotPasswordVerification: (mfaSecret: string) => Promise<void>;
    submitResetPassword: (loginId: string, newPassword: string, confirmNewPassword: string) => Promise<void>;
    error?: string;
    success?: string;
    forgotPasswordAttempt?: ForgotPasswordAttempt;
}

const defaultValue = {
    updateForgotPasswordSendMfa: async () => {},
    updateForgotPasswordVerification: async () => {},
    submitResetPassword: async () => {},
};

const ForgotPasswordContext = React.createContext<ForgotPasswordContextValue>(defaultValue)

interface ForgotPasswordContextProviderProps {
    children: ReactNode
}

export function ForgotPasswordContextProvider({children}: ForgotPasswordContextProviderProps) {

    const [forgotPasswordAttempt, setForgotPasswordAttempt] = useState<ForgotPasswordAttempt>();
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    
    useEffect(() => {
        (async () => {
            const searchParams = new URLSearchParams(window.location.search);
            const forgotPasswordAttemptId = searchParams.get('attemptId');
            let forgotPasswordSessionId = sessionStorage.getItem('forgotPasswordSessionId');
            if (forgotPasswordSessionId == null || forgotPasswordSessionId === '') {
                forgotPasswordSessionId = generateSessionId()
                sessionStorage.setItem('forgotPasswordSessionId', forgotPasswordSessionId);
            }
            let url = `/api/v2/sign-in-attempts/forgot-password/${forgotPasswordAttemptId}?forgotPasswordSessionId=${forgotPasswordSessionId}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            });
            const body = await response.json();
            if (response.ok) {
                setForgotPasswordAttempt(body)
            }
        })();
    }, []);

    useEffect(() => {
        if (error) {
            setTimeout(() => setError(undefined), 5000);
        }
    }, [error])

    function generateSessionId() {
        length = 16
        let chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
        var result = '';
        for (var i = length; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
        return result;
    }

    const updateForgotPasswordSendMfa = useCallback(async (mfaType: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const forgotPasswordAttemptId = searchParams.get('attemptId');
        const token = searchParams.get('token');

        let forgotPasswordSessionId = sessionStorage.getItem('forgotPasswordSessionId');
        let url = `/api/v2/sign-in-attempts/forgot-password/${forgotPasswordAttemptId}?forgotPasswordSessionId=${forgotPasswordSessionId}`;
        const getResponse = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (getResponse.ok) {
            const body = await getResponse.json();
            console.log(body)
            if (body.isPasswordResetComplete == 1) {
                setError("Password reset was already completed.")
            } else {
                let url = `/api/v2/sign-in-attempts/forgot-password-continuation`;

                const response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    //changing this to mfa code
                    body: JSON.stringify({mfaType, forgotPasswordAttemptId, token}),
                });

                if (response.ok) {
                    const body = await response.json();
                    setForgotPasswordAttempt(body)
                    if (body.isPasswordResetComplete == 1){
                        setError("Password reset was already completed.")
                    } else if (body.isMfaSecretSent == 0) {
                        setError("Please try again")
                    } else if (body.isMfaSecretSent == 1){
                        setSuccess("A verification code has been sent")
                    }
                } else if (response.status === 504) {
                    setError('The server failed to respond');
                } else if (response.status >= 400 && response.status < 500) {
                    const body = await response.json();
                    setError('Error: Some of the information provided below is not valid. Please try again.');
                } else if (response.status >= 500) {
                    setError('The server is unavailable right now, please try again later');
                } else {
                    const body = await response.json();
                    setError(body.displayMessage)
                }
            }
        }
    }, []);

    const updateForgotPasswordVerification = useCallback(async (mfaSecret: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const forgotPasswordAttemptId = searchParams.get('attemptId');
        const token = searchParams.get('token');

        let forgotPasswordSessionId = sessionStorage.getItem('forgotPasswordSessionId');
        let url = `/api/v2/sign-in-attempts/forgot-password/${forgotPasswordAttemptId}?forgotPasswordSessionId=${forgotPasswordSessionId}`;

        const getResponse = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (getResponse.ok) {
            const body = await getResponse.json();
            if (body.isPasswordResetComplete == 1) {
                setError("Password reset was already completed.")
            } else {
                let url = `/api/v2/sign-in-attempts/forgot-password-verification`;

                const response = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    //changing this to mfa code
                    body: JSON.stringify({mfaSecret, forgotPasswordAttemptId, token}),
                });

                if (response.ok) {
                    const body = await response.json();
                    setForgotPasswordAttempt(body)
                    if (body.isPasswordResetComplete == 1){
                        setError("Password reset was already completed.")
                    } else if (body.isVerificationComplete == 0) {
                        setError("Please try again")
                    } else if (body.isVerificationComplete == 1){
                        setSuccess("Verification complete")
                    }
                } else if (response.status === 504) {
                    setError('The server failed to respond');
                } else if (response.status >= 400 && response.status < 500) {
                    const body = await response.json();
                    setError(body.displayMessage);
                } else if (response.status >= 500) {
                    setError('The server is unavailable right now, please try again later');
                } else {
                    const body = await response.json();
                    setError(body.displayMessage)
                }
            }
        }
    }, []);

    const submitResetPassword = useCallback(async (loginId: string, newPassword: string, confirmNewPassword: string) => {
        const searchParams = new URLSearchParams(window.location.search);
        const forgotPasswordAttemptId = searchParams.get('attemptId');
        const token = searchParams.get('token');

        let url = `/api/v2/sign-in-attempts/reset-password`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({loginId, newPassword, confirmNewPassword, forgotPasswordAttemptId, token}),
        });

        if (response.ok) {
            const body = await response.json();
            if (body.isPasswordResetComplete == 1){
                setSuccess('Password reset was successful. Please login with your new password.')
            } else {
                setError('Password reset failed.')
            }
            setForgotPasswordAttempt(body)
            sessionStorage.setItem('forgotPasswordSessionId', '');
        } else if (response.status === 504) {
            setError('The server failed to respond');
        } else if (response.status >= 400 && response.status < 500) {
            const body = await response.json();
            const validationErrors = body.validationErrors
            // @ts-ignore
            let errors = validationErrors.filter((item) => item.displayMessage).map((o) => setError(o.displayMessage))
        } else if (response.status >= 500) {
            setError('The server is unavailable right now, please try again later');
        } else {
            const body = await response.json();
            setError(body.displayMessage);
        }
    }, []);
    
    return (
        <ForgotPasswordContext.Provider value={{forgotPasswordAttempt, updateForgotPasswordSendMfa, updateForgotPasswordVerification, submitResetPassword, success, error}}>
            {children}
        </ForgotPasswordContext.Provider>
    );
}

export const useForgotPasswordContext = () => useContext<ForgotPasswordContextValue>(ForgotPasswordContext);